/**
 * K&K Advertising (Voluum → Meta) click tracking, browser side.
 *
 * The Voluum loader at the bottom of <head> in index.html defines
 * window.dtpCallback. Once go.knkads.net/g/.js loads it runs our callback,
 * which keeps the Voluum click ID (and the landing fbclid) for 30 days.
 * The saved click is sent with:
 *   - the /demo booking → /api/track-booking → K&K Lead postback
 *   - Stripe checkouts (Payment Link client_reference_id, chatbot checkout
 *     metadata) → /api/stripe-webhook → K&K Purchase postback
 *
 * Tracking is best-effort and must never break the site.
 * See docs/knk-voluum-tracking.md.
 */

const STORAGE_KEY = 'roofclaw_cid';
const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;

// Stripe only accepts client_reference_id matching [A-Za-z0-9_-]{1,200} and
// silently drops anything else. Metadata supports 196-character CIDs; the
// versioned Payment Link reference also needs room for the original timestamp.
// Keep in sync with api/_knk.js.
const CID_PATTERN = /^[A-Za-z0-9_-]{1,196}$/;
const STRIPE_REFERENCE_PREFIX = 'knk2_';

/** The saved K&K click ({ cid, fbclid, at }) if it is under 30 days old, else null. */
export function readCid() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const click = JSON.parse(raw);
        if (typeof click?.cid !== 'string' || !CID_PATTERN.test(click.cid) || !Number.isSafeInteger(click.at) || click.at <= 0 || click.at > Date.now() || !(Date.now() - click.at < THIRTY_DAYS)) {
            localStorage.removeItem(STORAGE_KEY);
            return null;
        }
        return click;
    } catch {
        return null;
    }
}

/** Extra body fields for checkout sessions we create server-side (api/chat/checkout.js). */
export function knkCheckoutFields() {
    const click = readCid();
    return click ? { knk_cid: click.cid, knk_click_at: click.at } : {};
}

/**
 * Click handler: tag Stripe Payment Links with the saved click so the
 * webhook can match the purchase to the ad click. Runs at click time because
 * the cid may arrive after the link renders.
 */
export function tagStripePaymentLink(event) {
    const link = event.target?.closest?.('a[href]');
    if (!link) return;

    let url;
    try {
        url = new URL(link.href);
    } catch {
        return;
    }
    if (url.hostname !== 'buy.stripe.com' || url.searchParams.has('client_reference_id')) return;

    const click = readCid();
    if (!click || click.cid.length > 180) return;

    url.searchParams.set('client_reference_id', STRIPE_REFERENCE_PREFIX + click.at + '_' + click.cid);
    link.href = url.toString();
}

/** Call once from main.jsx, before the router renders. */
export function initKnkTracking() {
    try {
        // read it on page load, before the router can change the URL
        const landingFbclid = new URLSearchParams(location.search).get("fbclid") || undefined;

        // The inline loader defines dtpCallback. Skip quietly if it was removed.
        // Resolve the global again inside the callback: Voluum's script may replace it on load.
        if (typeof window.dtpCallback === 'function') {
            window.dtpCallback(() => {
                try {
                    const cid = window.dtpCallback.getClickID();
                    if (!cid || readCid()) return;
                    localStorage.setItem("roofclaw_cid", JSON.stringify({ cid, fbclid: landingFbclid, at: Date.now() }));
                } catch { /* storage blocked or loader changed: never break the page */ }
            });
        }

        document.addEventListener('click', tagStripePaymentLink, true);
        document.addEventListener('auxclick', tagStripePaymentLink, true);
    } catch (err) {
        console.error('K&K tracking init', err);
    }
}

let sentLeadCid;
/** Wait for the email-bearing booking message; coalesce repeated messages for the same click. */
export function knkBookingFields(email) {
    const click = readCid();
    if (!click) return {};
    if (typeof email !== 'string' || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return null;
    if (sentLeadCid === click.cid) return null;
    sentLeadCid = click.cid;
    return { email: email.trim(), knk_cid: click.cid, knk_fbclid: click.fbclid, knk_click_at: click.at };
}
